import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useEffect } from 'react';
import { MediaQuery } from '@grandvisionhq/common';
import { useVto, useCmsData, vtoEvent, ProductImageGalleryMobile, ProductImageGalleryView } from '@grandvisionhq/fun-products';
import { useIntl } from '@grandvisionhq/state';
import { placeholderImage, createImageUrl } from '@grandvisionhq/utils';
import { getActiveVariant } from '../helpers.js';
import { createAltTag } from '../utils/create-alt-tag.js';
import { pushImageGalleryItemExpandedEvent } from '../utils/DataLayer/Events/ImageGalleryEvents.js';
import { useProductDetailContext } from '../ProductDetail/Context/ProductDetailContext.js';

const defaultImageParams = {
    fit: 'fill',
    format: 'png'
};
const highlightedPackshots = new Set([
    'On_Model_Female01',
    'On_Model_Female02',
    'On_Model_Female03',
    'On_Model_Male01',
    'On_Model_Male02',
    'On_Model_Male03', 
]);
const ProductImageGalleryContainer = ()=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel , locale  } = useIntl();
    const [isImageVisible, setImageVisible] = React.useState(false);
    const [zoomMobileOpenIndex, setZoomMobileOpenIndex] = React.useState(null);
    const buttonClickedForZoomRef = React.useRef(null);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { isVtoActive , setVtoActive , isVtoFeatureEnabled  } = useVto();
    const { activeVariant , product  } = useProductDetailContext();
    const { vtoBanner  } = useCmsData();
    const activeVariantProduct = getActiveVariant(product, activeVariant.sku);
    const handleGalleryExpansion = React.useCallback((expanded)=>{
        if (!isImageVisible && expanded) {
            setImageVisible(true);
        }
        setIsExpanded(expanded);
    }, [
        isImageVisible
    ]);
    React.useEffect(()=>{
        if (isImageVisible) {
            pushImageGalleryItemExpandedEvent(isExpanded);
        }
    }, [
        isImageVisible,
        isExpanded
    ]);
    const handleImageInteraction = React.useCallback((index)=>{
        var ref;
        if (index === null) (ref = buttonClickedForZoomRef.current) === null || ref === void 0 ? void 0 : ref.focus();
        setZoomMobileOpenIndex(index);
    }, []);
    const altTagLabels = React.useMemo(()=>({
            lens: getLabel(`products.unit.contactLens`),
            lenses: getLabel(`products.unit.contactLenses`),
            perBox: getLabel(`products.perBox`)
        })
    , [
        getLabel
    ]);
    const { initialImages: initialImages1 , expandableImages: expandableImages1 , enhancedImages: enhancedImages1  } = React.useMemo(()=>{
        var ref;
        // Use placeholder if there are no images
        const variantImages = ((ref = activeVariant.images) === null || ref === void 0 ? void 0 : ref.length) > 0 ? activeVariant.images : [
            placeholderImage
        ];
        const enhancedImages = variantImages.map((image)=>swcHelpers.objectSpread({}, image, {
                thumbnailUrl: createImageUrl(image.url, swcHelpers.objectSpread({}, defaultImageParams, {
                    width: 224
                })),
                altText: createAltTag({
                    variant: product.masterVariant,
                    locale,
                    prefix: image.label,
                    labels: altTagLabels
                }),
                highlighted: highlightedPackshots.has(image.label),
                zoom: !highlightedPackshots.has(image.label)
            })
        );
        const vtoImg = vtoBanner ? {
            url: vtoBanner.image.url,
            dimensions: {
                w: vtoBanner.image.original.width,
                h: vtoBanner.image.original.height
            },
            thumbnailUrl: createImageUrl(vtoBanner.image.url, swcHelpers.objectSpread({}, defaultImageParams, {
                width: 112,
                height: 63,
                fit: 'cover'
            })),
            altText: vtoBanner.image.description || vtoBanner.title,
            highlighted: false,
            zoom: false,
            label: 'vto',
            vto: true
        } : swcHelpers.objectSpread({}, enhancedImages[0], {
            label: 'vto',
            zoom: false,
            vto: true
        });
        if (isVtoActive && !isSmallScreen && activeVariantProduct.vtoEnabled) {
            enhancedImages.unshift(vtoImg);
        } else if (isVtoActive && activeVariantProduct.vtoEnabled) {
            enhancedImages.push(vtoImg);
        } else if (vtoBanner && isVtoFeatureEnabled && activeVariantProduct.vtoEnabled) {
            enhancedImages.push(vtoImg);
        }
        /**
     * In order to correctly follow the design we need to decide how many images we will initially render on load.
     * If there is a highlightedPackshot we have more space to render an extra image.
     *
     * This follows the following logic:
     * 1. If there are no highlightedPackshots: render 3 images.
     * 2. If only the first image is a highlightedPackshot: render 3 images.
     * 3. If the second OR third image is a highlightedPackshot: render 4 images.
     * 4. If the second AND third images are highlightedPackshots: render 3 images.
     */ const initialImageSize = enhancedImages.slice(1, 3).filter((image)=>image.highlighted === true
        ).length === 1 ? 4 : 3;
        const initialImages = enhancedImages.slice(0, initialImageSize);
        const expandableImages = enhancedImages.slice(initialImageSize);
        return {
            initialImages,
            expandableImages,
            enhancedImages
        };
    }, [
        activeVariant.images,
        vtoBanner,
        isVtoActive,
        isVtoFeatureEnabled,
        isSmallScreen,
        activeVariantProduct.vtoEnabled,
        product.masterVariant,
        locale,
        altTagLabels, 
    ]);
    useEffect(()=>{
        if (product.masterVariant.sku && product.masterVariant.vtoEnabled) {
            vtoEvent('vto.view', {
                sku: product.masterVariant.sku
            });
        }
    }, [
        product.masterVariant.sku,
        product.masterVariant.vtoEnabled
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, isSmallScreen ? /*#__PURE__*/ React.createElement(ProductImageGalleryMobile, {
        images: enhancedImages1,
        isVtoEnabled: isVtoFeatureEnabled,
        sku: activeVariantProduct.sku,
        imageParams: defaultImageParams,
        vtoBanner: vtoBanner,
        handleImageInteraction: handleImageInteraction,
        vtoActive: isVtoActive,
        setVtoActive: setVtoActive,
        zoomModalOpenIndex: zoomMobileOpenIndex
    }) : /*#__PURE__*/ React.createElement(ProductImageGalleryView, {
        initialImages: initialImages1,
        isVtoEnabled: isVtoFeatureEnabled,
        expandableImages: expandableImages1,
        showMoreLabel: getLabel('products.imageGallery.showMore'),
        showLessLabel: getLabel('products.imageGallery.showLess'),
        isImageVisible: isImageVisible,
        handleGalleryExpansion: handleGalleryExpansion,
        handleImageInteraction: handleImageInteraction,
        setVtoActive: setVtoActive,
        sku: activeVariantProduct.sku,
        vtoActive: isVtoActive,
        vtoBanner: vtoBanner,
        zoomModalOpenIndex: zoomMobileOpenIndex,
        buttonClickedForZoomRef: buttonClickedForZoomRef
    }));
};

export { ProductImageGalleryContainer };
//# sourceMappingURL=ProductImageGalleryContainer.js.map
